import { useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import {
  Chip,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'

import Button from '../components/Button'
import Dialog from '../components/Dialog'
import GridHeader from '../components/GridHeader'
import GridIconButton from '../components/GridIconButton'
import SelectionBar from '../components/selection/SelectionBar'
import TableCellTeam from '../components/table/TableCellTeam'
import SortableTableHead from '../components/SortableTableHead'
import GridCircularProgress from '../components/GridCircularProgress'
import { config } from '../settings/configs'
import { colours, titlesDistinte, phrasesDistinte } from '../settings/settings'
import { selectDistinteSorted } from '../store/selectors'
import { useFeedback } from '../hooks/feedback-hook'
import { useRunAlgorithm } from '../hooks/algoritmi-functions'
import {
  fromDateTimeToString,
  isDistintaReadOnly,
} from '../hooks/utils-functions'
import {
  useGetCalendariManyQuery,
  useGetDistinteQuery,
  useDeleteDistintaMutation,
  useGetIscrizioniQuery,
} from '../hooks/api-slice'
import { setDistinta } from '../container/distinte'

const columns = [
  { label: 'Creazione', sortable: false },
  { label: 'Categoria', sortable: false },
  { label: 'Gara', sortable: false },
  { label: 'Data e Ora', sortable: false },
  { label: 'Squadra in casa', sortable: false },
  { label: 'Squadra in trasferta', sortable: false },
  { label: '', sortable: false },
]

const Distinte = () => {
  const { admin, codiceUtente } = useSelector(state => state.home.infoUtente)
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const availableCategorie = useSelector(
    state => state.selection.availableCategorie,
  )
  const selectedCategoria = useSelector(
    state => state.selection.selectedCategoria,
  )
  const selectedNumeroGara = useSelector(
    state => state.selection.selectedNumeroGara,
  )

  const dArgs = { admin, selectedCampionato, codiceUtente }
  const dSkip = !((admin || codiceUtente) && selectedCampionato)
  const { isFetching: isFetchingD } = useGetDistinteQuery(dArgs, {
    skip: dSkip,
  })
  const distinte = useSelector(state => selectDistinteSorted(state, dArgs))

  const { isFetching: isFetchingI } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, iFilter: 'nomi' },
    { skip: !selectedCampionato },
  )

  const idGare = distinte?.map(d => d.idGara)
  const { data: distinteGare, isFetching: isFetchingG } =
    useGetCalendariManyQuery(idGare, { skip: !idGare?.length })

  const distinteN = useMemo(
    () =>
      distinte?.map(d => {
        const dGara = distinteGare?.find(g => g._id === d.idGara)
        return {
          data: dGara?.data,
          ora: dGara?.ora,
          categoria: dGara?.categoria,
          numeroGara: dGara?.numeroGara,
          squadraCasaID: dGara?.squadraCasaID,
          squadraTrasfertaID: dGara?.squadraTrasfertaID,
          ...d,
        }
      }),
    [distinte, distinteGare],
  )

  const distinteF = distinteN?.filter(
    d =>
      (selectedCategoria === availableCategorie[0] ||
        d?.categoria === selectedCategoria) &&
      (!selectedNumeroGara || d?.numeroGara === selectedNumeroGara),
  )

  const allReadOnly = distinteF?.every(d => {
    const dGara = distinteGare?.find(g => g._id === d.idGara)
    return isDistintaReadOnly(admin, dGara)
  })

  const [deleteDistintaMutation] = useDeleteDistintaMutation()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { setFeedback } = useFeedback()
  const { runAlgorithm } = useRunAlgorithm()

  document.title = `${config?.name} - Distinte`

  const [openDelete, setOpenDelete] = useState()
  const [deletingDistinta, setDeletingDistinta] = useState()

  const handleClickOpenDelete = i => {
    setDeletingDistinta(i)
    setOpenDelete(true)
  }

  const handleCloseDelete = () => {
    setOpenDelete()
    setDeletingDistinta()
  }

  const editDistinta = async x => {
    dispatch(setDistinta(x))
    navigate('/distinta')
  }

  const deleteDistinta = async () => {
    const result = await deleteDistintaMutation(deletingDistinta?._id)
    if (!result?.error) {
      setFeedback('Distinta eliminata correttamente.', false)
      handleCloseDelete()
    }
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Distinte' />
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar
          data={distinteN}
          yCampionato
          yCategoria
          yNumeroGara={admin}
        />
        {!admin && (
          <Grid size={{ xs: 12, md: 'grow' }}>
            <center>
              <Button
                to='/distinta'
                onClick={() => dispatch(setDistinta())}
                children='Nuova distinta'
              />
            </center>
          </Grid>
        )}
      </Grid>
      {((isFetchingD || isFetchingI || isFetchingG) && (
        <GridCircularProgress />
      )) ||
        (!distinte && (
          <GridHeader text='Elenco delle distinte non trovato.' />
        )) ||
        (distinteF.length === 0 && (
          <GridHeader text='Non è ancora stata creata nessuna distinta.' />
        )) || (
          <Grid container sx={{ width: '100%' }}>
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHead columns={columns} />
                <TableBody>
                  {distinteF.map((d, index) => {
                    const dTimeFirstInsert =
                      d.timeFirstInsert &&
                      fromDateTimeToString(new Date(d.timeFirstInsert), true)
                    const dDataOra = d?.data && d?.ora && `${d?.data} ${d?.ora}`

                    const readOnly = isDistintaReadOnly(admin, d)

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: colours.white,
                          ':hover': { backgroundColor: colours.greyVeryLight },
                        }}
                        onClick={() => editDistinta(d)}
                      >
                        <TableCell>{dTimeFirstInsert}</TableCell>
                        <TableCell>
                          <Chip variant='outlined' label={d?.categoria} />
                        </TableCell>
                        <TableCell>{d?.numeroGara}</TableCell>
                        <TableCell>{dDataOra}</TableCell>
                        <TableCellTeam
                          id={d?.squadraCasaID}
                          bold={d?.squadraCasaID === d?.idSquadra}
                        />
                        <TableCellTeam
                          id={d?.squadraTrasfertaID}
                          bold={d?.squadraTrasfertaID === d?.idSquadra}
                        />
                        <TableCell>
                          <Grid container wrap='nowrap' sx={{ width: '100%' }}>
                            <Grid size='grow'>
                              <IconButton
                                style={{ color: colours.greenExcel }}
                                onClick={e => {
                                  e.stopPropagation()
                                  runAlgorithm({
                                    algorithm: '20_GenerateDistinta',
                                    selectedDistinta: d._id,
                                    exactlyOne: true,
                                    type: 'PDF',
                                  })
                                }}
                              >
                                <Tooltip title='Scarica PDF'>
                                  <DownloadIcon />
                                </Tooltip>
                              </IconButton>
                            </Grid>
                            {!allReadOnly &&
                              ((readOnly && <Grid size='grow' />) || (
                                <GridIconButton
                                  type='delete'
                                  onClick={e => {
                                    e.stopPropagation()
                                    handleClickOpenDelete(d)
                                  }}
                                />
                              ))}
                          </Grid>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      <Dialog
        open={openDelete}
        handleClose={handleCloseDelete}
        title={titlesDistinte.titleDelete}
        textContent={phrasesDistinte.phraseDelete}
        textUndo='Annulla'
        textConfirm='Ok'
        triggerFunction={deleteDistinta}
        error
      />
    </Grid>
  )
}

export default Distinte
